<template>
    <div class="search-autocomplete" :class="{'search-autocomplete--clearable': clearable}">
        <input
            type="text"
            :value="query"
            :placeholder="placeholder"
            :maxlength="maxlength"
            autocomplete="off"
            :data-testid="testId ? `${testId}-field` : null"
            :aria-label="placeholder"
            @input="onInput"
            @focus="onFocus"
            @blur="onBlur"
        />
        <button v-if="clearable && query?.length" :data-testid="testId ? `${testId}-clear` : null" @click="clear">
            <VJoyIcon name="cross" color="neutral" size="xxsmall" />
        </button>
        <Transition name="fade-scale" mode="out-in">
            <ul
                v-if="suggestions?.length && showSuggestions"
                class="search-autocomplete__suggestions"
                tabindex="-1"
                :data-testid="testId ? `${testId}-suggestions` : null"
            >
                <li
                    v-for="(suggestion, i) in suggestions"
                    ref="suggestionsRefs"
                    :key="i"
                    :class="{'search-autocomplete--selected': i === listIndex}"
                    @mousedown.prevent.stop="select(i)"
                >
                    <slot name="suggestion" :suggestion="suggestion">
                        {{ suggestion }}
                    </slot>
                </li>
            </ul>
        </Transition>
    </div>
</template>

<script setup lang="ts">
    import {ref, useTemplateRef, watch} from 'vue';
    import {onKeyStroke} from '@vueuse/core';
    import {VJoyIcon} from '@maltjoy/core-vue';

    const isFocused = ref(false);

    const {
        suggestions,
        modelValue,
        formatter = (val: any) => val,
    } = defineProps<{
        modelValue: any;
        placeholder?: string;
        suggestions?: string[] | Record<string, any>[];
        maxlength?: number;
        testId?: string;
        clearable?: boolean;
        formatter?: (val: any) => string;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: string | Record<string, unknown> | undefined];
        select: [suggestion: string | Record<string, unknown> | undefined];
    }>();

    const query = ref(formatter(modelValue) || '');
    const listIndex = ref(0);
    const blockSuggestions = ref(false);
    const showSuggestions = ref(false);
    const suggestionsRefs = useTemplateRef('suggestionsRefs');

    watch(
        () => suggestions,
        () => {
            if (suggestions?.length && !blockSuggestions.value) {
                showSuggestions.value = true;
            }
        },
    );

    function onInput(event: Event) {
        const {value} = event.target as HTMLInputElement;
        query.value = value;
        emit('update:modelValue', value);
    }

    function onFocus() {
        isFocused.value = true;
        if (suggestions?.length) {
            showSuggestions.value = true;
        }
    }

    function onBlur() {
        isFocused.value = false;
        showSuggestions.value = false;
    }

    function select(index: number) {
        blockSuggestions.value = true;
        showSuggestions.value = false;

        const s = suggestions![index];
        emit('update:modelValue', s);
        emit('select', s);
        query.value = formatter(s);

        setTimeout(() => {
            blockSuggestions.value = false;
        }, 250);
    }

    function clear() {
        query.value = '';
        emit('update:modelValue', '');
    }
    onKeyStroke(['ArrowUp', 'ArrowDown'], (e: KeyboardEvent) => {
        if (!showSuggestions.value) {
            return;
        }
        const increment = e.key === 'ArrowUp' ? -1 : 1;
        const length = suggestions!.length;
        listIndex.value = (listIndex.value + increment + length) % length;

        const selectedItem = suggestionsRefs.value?.[listIndex.value];
        selectedItem?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
    });

    onKeyStroke('Enter', (e: KeyboardEvent) => {
        if (showSuggestions.value && suggestions?.length) {
            select(listIndex.value);
            e.stopPropagation();
        }
    });

    onKeyStroke('Escape', () => {
        showSuggestions.value = false;
    });
</script>
<style lang="scss">
    .search-autocomplete {
        input {
            text-overflow: ellipsis;
        }
        &--clearable input {
            padding-right: var(--joy-core-spacing-6);
        }
        button {
            position: absolute;
            right: var(--joy-core-spacing-2);
            top: 19px;
            cursor: pointer;
            line-height: 0;
            background: transparent;
            border: 1px solid transparent;
            padding: 6px;
            opacity: 0.5;

            &:hover,
            &:focus {
                opacity: 1;
                border-color: var(--joy-color-neutral-40);
                border-style: dotted;
                border-radius: var(--joy-core-radius-4);
            }
        }
        &__suggestions {
            position: absolute;
            z-index: 10;
            top: 100%;
            left: 0;
            width: 100%;
            background-color: var(--joy-color-background-default);
            border-radius: var(--joy-core-radius-3);
            margin-top: var(--joy-core-spacing-3);
            padding: 0;
            color: var(--joy-color-neutral-60);
            // overflow: hidden;
            box-shadow: var(--joy-core-elevation-2);
            max-height: calc(5 * 36px);
            overflow: auto;

            > li {
                padding: var(--joy-core-spacing-2) var(--joy-core-spacing-5);
                cursor: pointer;

                &:hover {
                    background-color: var(--joy-color-secondary-10);
                    color: var(--joy-color-neutral-70);
                }
            }
        }

        &--selected {
            background-color: var(--joy-color-secondary-10);
            color: var(--joy-color-neutral-70);
        }
    }

    /* - Fade and scale transition */
    .fade-scale-enter-active,
    .fade-scale-leave-active {
        transition-property: opacity transform;
        transition-duration: 0.15s;
        transition-timing-function: ease;
    }

    .fade-scale-leave-active {
        transition-duration: 0.05s;
    }

    .fade-scale-enter-to {
        transform: scale(1);
    }

    .fade-scale-enter-from,
    .fade-scale-leave-to {
        opacity: 0;
        transform: scale(0.95);
    }
</style>
